import { useTracking } from '@vp/react-tracking';
import { useEffect, useState } from 'react';
import {
    Button,
    Card,
    FlexBox,
    ModalDialog,
    ModalDialogBody,
    ModalDialogButtons,
    ModalDialogCloseButton,
    ModalDialogContent,
    ModalDialogFooter,
    ModalDialogHeader,
    ModalDialogNav,
    ModalDialogTitle,
    Typography,
    responsive,
} from '@vp/swan';
import type { FC } from 'react';
import { bugtracker } from '@99designs/design-services-common';
import { Request } from '@99designs/graph-utils/types';
import { __ } from '@99designs/i18n';
import { DS_BRIEF_MULTI_STEP_URL } from '@99designs/i18n';
import { useCancelDashboardBriefMutation } from '../queries/CancelDashboardBrief.generated';
import { CTA, RequestCardCTA } from './RequestCardCTA';
import { RequestCardImage } from './RequestCardImage';
import { RequestCardSummary } from './RequestCardSummary';

const ResponsiveFlexBox = responsive(FlexBox);

interface DraftBriefCardProps {
    request: Request;
    addToDeletedQueue: (title: string) => void;
    addToErrorQueue: () => void;
    refetchDraftBriefs: () => void;
}

const DraftBriefCard: FC<DraftBriefCardProps> = ({
    request,
    addToDeletedQueue,
    addToErrorQueue,
    refetchDraftBriefs,
}) => {
    const { tracking } = useTracking();

    const [isDeleteConfirmationShown, setIsDeleteConfirmationShown] = useState(false);

    const [cancelMutation, { data: cancelData, loading: cancelLoading, error: cancelError }] =
        useCancelDashboardBriefMutation();

    const brief = request.brief;
    const printProductName = request.expertService?.printProduct?.name ?? '';
    const title =
        brief?.title ||
        __(`Untitled ${printProductName.toLowerCase()} design project`, { printProductName });
    const briefUrl = `${DS_BRIEF_MULTI_STEP_URL}/${brief?.id}?step=1`;

    useEffect(() => {
        if (cancelError && !cancelLoading) {
            bugtracker.notify(new Error('error cancelling draft brief'), (event) => {
                event.addMetadata('debug info', 'request_id', request.id);
                event.addMetadata('debug info', 'title', title);
                event.addMetadata('debug info', 'brief_url', briefUrl);
                event.addMetadata('debug info', 'error', cancelError.message);
            });

            addToErrorQueue();
            setIsDeleteConfirmationShown(false);
        }
    }, [cancelError, cancelData, cancelLoading, request.id, title, briefUrl, addToErrorQueue]);

    useEffect(() => {
        if (cancelData && !cancelLoading) {
            if (cancelData.document) {
                addToDeletedQueue(title);

                // Cancelling removes an item from the page; we need to refresh the list.

                void refetchDraftBriefs();
            } else {
                // Cancel failed but didn't error. Currently handled in the same way as an error.

                bugtracker.notify(
                    new Error('error cancelling draft brief; no success returned'),
                    (event) => {
                        event.addMetadata('debug info', 'request_id', request.id);
                        event.addMetadata('debug info', 'title', title);
                        event.addMetadata('debug info', 'brief_url', briefUrl);
                    }
                );

                addToErrorQueue();
            }

            // Even if there's an error, close the modal anyway to prevent confusion.

            setIsDeleteConfirmationShown(false);
        }
    }, [
        cancelData,
        cancelLoading,
        addToDeletedQueue,
        title,
        request.id,
        briefUrl,
        refetchDraftBriefs,
        addToErrorQueue,
    ]);

    if (!brief) {
        return null;
    }

    const analytics = {
        section: 'draft',
        category: 'Design Services',
        pageName: 'Design Services:Dashboard',
        pageSection: 'Design Services',
        pageStage: 'My Account',
    };

    const ctas: CTA[] = [
        {
            label: __('Complete brief'),
            iconType: 'edit',
            href: briefUrl,
            onClick: () => {
                tracking.track('Navigation Clicked', {
                    ...analytics,
                    action: 'Navigation Clicked',
                    navigationDetail: 'complete-brief',
                });
            },
            openInNewTab: false,
            skin: 'primary',
        },
        {
            label: __('Delete brief'),
            iconType: 'delete',
            onClick: () => {
                setIsDeleteConfirmationShown(true);
                tracking.track('Action Clicked', {
                    ...analytics,
                    action: 'Action Clicked',
                    actionDetail: 'delete-brief',
                });
            },
            openInNewTab: false,
            skin: 'tertiary',
        },
    ];

    const closeDeleteConfirmation = () => {
        setIsDeleteConfirmationShown(false);
    };

    const confirmDeleteConfirmation = () => {
        void cancelMutation({
            variables: {
                id: brief.id,
            },
        });
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    return (
        <>
            <Card bordered>
                <ResponsiveFlexBox
                    xs={{
                        flexDirection: 'column',
                        gap: 'between-subsections',
                        alignItems: 'center',
                    }}
                    sm={{
                        flexDirection: 'row',
                        gap: 'between-sections',
                        alignItems: 'flex-start',
                        justifyContent: 'space-between',
                    }}
                >
                    {/* Project image thumbnail */}
                    <RequestCardImage
                        imgSrc="https://cms.cloudinary.vpsvc.com/image/upload/fl_sanitize,q_auto/expert_services/Dashboard/Draft_brief_graphic_617_570.svg"
                        imgAlt={__('Draft Brief')}
                    />

                    {/* Project details */}
                    <ResponsiveFlexBox
                        style={{ flex: '1 1 100%' }}
                        xs={{
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: 'between-actions',
                        }}
                        sm={{ alignItems: 'flex-start' }}
                    >
                        <RequestCardSummary
                            title={truncateWithEllipsis(title, 70)}
                            linkUrl={briefUrl}
                            createdAt={brief.createdAt}
                            productName={printProductName}
                            analytics={analytics}
                            status="DRAFT_BRIEF_REQUEST_IN_PROGRESS"
                        />
                        <Typography fontSkin="body-standard">
                            {truncateWithEllipsis(brief.description, 200)}
                        </Typography>
                    </ResponsiveFlexBox>
                    {/* Project CTA */}
                    <RequestCardCTA ctas={ctas} />
                </ResponsiveFlexBox>
            </Card>
            <ModalDialog
                isOpen={isDeleteConfirmationShown}
                onRequestDismiss={closeDeleteConfirmation}
            >
                <ModalDialogContent aria-labelledby="">
                    <ModalDialogNav>
                        <ModalDialogCloseButton accessibleText={__('Close')} />
                    </ModalDialogNav>
                    <ModalDialogHeader>
                        <ModalDialogTitle>
                            {__('Delete draft brief?', {
                                _context:
                                    'Title of the confirmation that appears when clicking to delete a draft brief.',
                            })}
                        </ModalDialogTitle>
                    </ModalDialogHeader>
                    <ModalDialogBody>
                        {__('Are you sure you want to delete {{title}}? Deleting is permanent.', {
                            _context:
                                'Text of the confirmation that appears when clicking to delete a draft brief.',
                            title,
                        })}
                    </ModalDialogBody>
                    <ModalDialogFooter>
                        <ModalDialogButtons>
                            <Button skin="secondary" onClick={closeDeleteConfirmation}>
                                {__('Cancel')}
                            </Button>
                            <Button
                                data-testid="confirm-delete-brief-button"
                                onClick={confirmDeleteConfirmation}
                                skin="destructive-primary"
                                loading={cancelLoading}
                            >
                                {__('Delete brief')}
                            </Button>
                        </ModalDialogButtons>
                    </ModalDialogFooter>
                </ModalDialogContent>
            </ModalDialog>
        </>
    );
};

export { DraftBriefCard };

function truncateWithEllipsis(
    text: string | undefined | null,
    maxLength: number
): string | undefined {
    if (!text) return;

    if (text.length <= maxLength) {
        return text;
    }
    return text.slice(0, maxLength - 3) + '...';
}
